import { featureModuleTypesEnum } from "helpers/enums";
import { canManageOrganization } from "helpers/system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Popover } from "react-tiny-popover";

const LEGEND_BOUNDARY_INSET = 8;
const LEGEND_GAP = 8;
const LEGEND_MAX_WIDTH = 540;
const LEGEND_MIN_WIDTH = 220;
const LEGEND_MIN_HEIGHT = 120;
const LEGEND_RIGHT_MIN_WIDTH = 300;
const LEGEND_TWO_COLUMNS_MIN_WIDTH = 500;

const defaultLegendLayout = {
    align: "end",
    columns: 2,
    maxHeight: 560,
    position: "right",
    width: LEGEND_MAX_WIDTH,
};

const getMapElement = () => {
    if (typeof document === "undefined") {
        return null;
    }

    return document.getElementById("modal-map") || document.getElementById("map");
};

const clampDimension = (value, min, max) => {
    const safeMax = Math.max(0, max);

    if (safeMax < min) {
        return safeMax;
    }

    return Math.max(min, Math.min(value, safeMax));
};

const BidDataLegend = ({ availableFeatureModules, roles }) => {
    const [listOpened, setListOpened] = useState(false);
    const buttonRef = useRef(null);
    const [legendLayout, setLegendLayout] = useState(defaultLegendLayout);

    const updateLegendLayout = useCallback(() => {
        const mapElement = getMapElement();
        const buttonElement = buttonRef.current;

        if (!mapElement || !buttonElement) {
            setLegendLayout(defaultLegendLayout);
            return;
        }

        const mapRect = mapElement.getBoundingClientRect();
        const buttonRect = buttonElement.getBoundingClientRect();
        const availableRight = mapRect.right - buttonRect.right - LEGEND_GAP - LEGEND_BOUNDARY_INSET;
        const availableFromButtonLeft = mapRect.right - buttonRect.left - LEGEND_BOUNDARY_INSET;
        const shouldOpenRight = availableRight >= LEGEND_RIGHT_MIN_WIDTH;
        const widthLimit = shouldOpenRight ? availableRight : availableFromButtonLeft;
        const width = Math.floor(clampDimension(LEGEND_MAX_WIDTH, LEGEND_MIN_WIDTH, widthLimit));
        const maxHeightLimit = shouldOpenRight
            ? buttonRect.bottom - mapRect.top - LEGEND_BOUNDARY_INSET
            : buttonRect.top - mapRect.top - LEGEND_GAP - LEGEND_BOUNDARY_INSET;
        const maxHeight = Math.floor(clampDimension(maxHeightLimit, LEGEND_MIN_HEIGHT, maxHeightLimit));
        const nextLayout = {
            align: shouldOpenRight ? "end" : "start",
            columns: width >= LEGEND_TWO_COLUMNS_MIN_WIDTH ? 2 : 1,
            maxHeight,
            position: shouldOpenRight ? "right" : "top",
            width,
        };

        setLegendLayout((currentLayout) =>
            currentLayout.align === nextLayout.align &&
            currentLayout.columns === nextLayout.columns &&
            currentLayout.maxHeight === nextLayout.maxHeight &&
            currentLayout.position === nextLayout.position &&
            currentLayout.width === nextLayout.width
                ? currentLayout
                : nextLayout,
        );
    }, []);

    useEffect(() => {
        if (!listOpened) {
            return undefined;
        }

        updateLegendLayout();
        window.addEventListener("resize", updateLegendLayout);

        const mapElement = getMapElement();
        const resizeObserver =
            mapElement && typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateLegendLayout) : null;

        if (resizeObserver) {
            resizeObserver.observe(mapElement);
        }

        return () => {
            window.removeEventListener("resize", updateLegendLayout);
            resizeObserver?.disconnect();
        };
    }, [listOpened, updateLegendLayout]);

    const toggleList = useCallback(() => {
        if (!listOpened) {
            updateLegendLayout();
        }

        setListOpened(!listOpened);
    }, [listOpened, updateLegendLayout]);

    const popoverContent = (
        <div
            className={`map-bid-legend__list${
                legendLayout.columns === 1 ? " map-bid-legend__list--single-column" : ""
            }`}
            style={{
                "--map-bid-legend-max-height": `${legendLayout.maxHeight}px`,
                "--map-bid-legend-width": `${legendLayout.width}px`,
            }}
        >
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--planned-platon" />
                <div className="d-inline-block">Плановый маршрут по федеральным трассам (Платон)</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--planned" />
                <div className="d-inline-block">Плановый маршрут по региональным трассам</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--planned-toll" />
                <div className="d-inline-block">Плановый маршрут по платным дорогам</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--empty-planned" />
                <div className="d-inline-block">Плановый порожний маршрут</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--fact" />
                <div className="d-inline-block">Фактический маршрут</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--empty-fact" />
                <div className="d-inline-block">Фактический порожний маршрут</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--emulated" />
                <div className="d-inline-block">Эмулированный маршрут</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--emulated-fact" />
                <div className="d-inline-block">Эмулированный порожний маршрут</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--actual" />
                <div className="d-inline-block">Неучтенный маршрут</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__line map-bid-legend__line--mobile" />
                <div className="d-inline-block">Маршрут по мобильным координатам</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="b-timeline-point__circle">
                        <div className="b-timeline-point__circle--text">A</div>
                    </div>
                </div>
                <div className="d-inline-block">Точки заявки</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="b-timeline-point__circle">
                        <div className="b-timeline-point__circle--text b-timeline-point__circle--refueling" />
                    </div>
                </div>
                <div className="d-inline-block">Заправки</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="map-bid-legend__point--between" />
                </div>
                <div className="d-inline-block">Промежуточные точки заявки</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="map-bid-legend__point--fact" />
                </div>
                <div className="d-inline-block">Точки фактического маршрута</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="map-bid-legend__point--stops" />
                </div>
                <div className="d-inline-block">Точки остановок</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="map-bid-legend__point--control" />
                </div>
                <div className="d-inline-block">Контрольные точки</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="map-bid-legend__point--item-changes" />
                </div>
                <div className="d-inline-block">Перецепки/пересменки</div>
            </div>
            {availableFeatureModules.includes(featureModuleTypesEnum.DocumentAssignment) && (
                <div className="map-bid-legend__list-item">
                    <div className="map-bid-legend__point">
                        <div className="map-bid-legend__point--postamats" />
                    </div>
                    <div className="d-inline-block">Постаматы</div>
                </div>
            )}
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="map-bid-legend__point--platon" />
                </div>
                <div className="d-inline-block">Рамки Платон</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="map-bid-legend__point--emergency-area-point" />
                </div>
                <div className="d-inline-block">Аварийные участки</div>
            </div>
            <div className="map-bid-legend__list-item">
                <div className="map-bid-legend__point">
                    <div className="map-bid-legend__point--payment-point" />
                </div>
                <div className="d-inline-block">Пункты оплаты</div>
            </div>
            {availableFeatureModules.includes(featureModuleTypesEnum.RouteControlPoints) &&
                canManageOrganization(roles) && (
                    <div className="map-bid-legend__list-item">
                        <div className="map-bid-legend__point">
                            <div className="map-bid-legend__point--route-control-point" />
                        </div>
                        <div className="d-inline-block">Контрольные точки маршрута</div>
                    </div>
                )}
            {availableFeatureModules.includes(featureModuleTypesEnum.UnstableZones) && canManageOrganization(roles) && (
                <div className="map-bid-legend__list-item">
                    <div className="map-bid-legend__point">
                        <div className="map-bid-legend__point--unstable-zones" />
                    </div>
                    <div className="d-inline-block">Зоны нестабильных координат</div>
                </div>
            )}
        </div>
    );

    return (
        <div className="map-bid-legend">
            <Popover
                ref={buttonRef}
                isOpen={listOpened}
                positions={legendLayout.position}
                align={legendLayout.align}
                boundaryInset={LEGEND_BOUNDARY_INSET}
                padding={LEGEND_GAP}
                containerClassName="map-bid-legend__popover"
                parentElement={getMapElement() || undefined}
                content={popoverContent}
            >
                <div className="map-bid-legend__button" onClick={toggleList} />
            </Popover>
        </div>
    );
};

export default BidDataLegend;
